/**
 * Controller panel — a model-accurate operator twin. The facia skin is chosen by profile_id (DSE7420
 * MKII vs SmartGen HGM61xx; others fall back to the DSE skin until built), each recreating the
 * physical bezel, keys, LEDs and mimic, bound to the live /controllers/:id/panel feed — so a remote
 * operator sees and OPERATES what they would standing at the controller.
 *
 * The mode keys and Start/Stop issue real, safeguarded commands: operator role or above, the tenant
 * kill switch, and a typed site-name confirmation, with a pending→confirmed round trip so a key
 * never reports success the controller has not actually acknowledged. Arbitrary register
 * configuration lives in the Engineer panel, not here.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth';
import { ApiError, type ControllerPanel as Panel, type PanelPoint } from '../api';
import { Banner, Button } from '../ui';
import { ConnectivityWidget } from '../ConnectivityWidget';

const POLL_MS = 4000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * "1043h 27m" — how an operator reads a running-hours counter. The module reports it in seconds, and
 * nobody converts 3,756,420 in their head. Under a minute stays in seconds so a short timer still
 * reads honestly rather than collapsing to "0m".
 *
 * Matches the wording of the Telegram run reports so the same duration reads the same in both.
 */
export function duration(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  if (s < 60) return `${s} s`;
  const mins = Math.floor(s / 60);
  const h = Math.floor(mins / 60);
  return h > 0 ? `${h}h ${String(mins % 60).padStart(2, '0')}m` : `${mins}m`;
}

function fmt(p: PanelPoint | undefined): string {
  if (!p) return '—';
  if (p.enumLabel) return p.enumLabel;
  if (p.value === null || p.quality === 'unavailable') return '—';
  if (p.unit === 'Seconds') return duration(p.value);
  const v = Number.isInteger(p.value) ? p.value : Math.round(p.value * 10) / 10;
  return `${v}${p.unit ? ` ${p.unit}` : ''}`;
}

interface Row {
  lbl: string;
  val: string;
  big?: boolean;
}
type CmdState =
  | { kind: 'idle' }
  | { kind: 'sending'; action: string }
  | { kind: 'done'; ok: boolean; msg: string };

/* Facia glyphs — small inline SVGs so the panels read like the physical controllers. */
const ICON = {
  stop: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
      <circle cx="12" cy="12" r="7" />
    </svg>
  ),
  start: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
    >
      <path d="M12 5v14" />
    </svg>
  ),
  hand: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M8 11V6a1.5 1.5 0 013 0v4m0-1a1.5 1.5 0 013 0v1m0 0a1.5 1.5 0 013 0v4a5 5 0 01-5 5h-1a5 5 0 01-4-2.5L8 15" />
    </svg>
  ),
  mute: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="round"
    >
      <path d="M6 10a6 6 0 0112 0v4l2 3H4l2-3z" />
      <path d="M4 20L20 4" stroke="#c8392a" strokeWidth={2} />
    </svg>
  ),
  test: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path d="M8 4l-2 12M16 4l2 12M7 8h10M12 4v12" />
      <path d="M4 20l16-16" stroke="#c8392a" strokeWidth={2} />
    </svg>
  ),
  // Alarm reset: the bell of the mute key, with a round-trip arrow rather than a strike-through —
  // mute silences, reset clears, and the two must not read as the same action at a glance.
  reset: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="round"
      strokeLinecap="round"
    >
      <path d="M7 11a5 5 0 0110 0v3l1.6 2.5H5.4L7 14z" />
      <path d="M18.5 20.5a5.5 5.5 0 01-9.2-2.2" stroke="#3f9d5a" strokeWidth={2} />
      <path d="M18.9 17.4l.4 3.4-3.4.2" stroke="#3f9d5a" strokeWidth={2} />
    </svg>
  ),
  autoBox: (
    <svg viewBox="0 0 40 24">
      <rect
        x="1"
        y="4"
        width="38"
        height="16"
        rx="8"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
      />
      <text x="20" y="16" fontSize="9" fontWeight="800" textAnchor="middle" fill="currentColor">
        AUTO
      </text>
    </svg>
  ),
  spiral: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    >
      <path d="M12 4a8 8 0 11-6.9 4" />
      <path d="M12 8a4 4 0 100 8" />
    </svg>
  ),
  alarmTri: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4l9 16H3z" />
      <path d="M12 10v4M12 17v.5" />
    </svg>
  ),
  status: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 12a8 8 0 10-2.3 5.6M20 12v4h-4" />
    </svg>
  ),
  up: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 15l6-6 6 6" />
    </svg>
  ),
  down: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  home: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinejoin="round"
    >
      <path d="M4 11l8-6 8 6v8h-5v-5H9v5H4z" />
    </svg>
  ),
  gear: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M12 4v2M12 18v2M4 12h2M18 12h2M6 6l1.5 1.5M16.5 16.5L18 18M18 6l-1.5 1.5M7.5 16.5L6 18" />
    </svg>
  ),
  bkClose: (
    <svg
      viewBox="0 0 32 22"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <circle cx="6" cy="16" r="2.2" fill="currentColor" />
      <circle cx="26" cy="16" r="2.2" fill="currentColor" />
      <path d="M8 16h16" />
    </svg>
  ),
  bkOpen: (
    <svg
      viewBox="0 0 32 22"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
    >
      <circle cx="6" cy="16" r="2.2" fill="currentColor" />
      <circle cx="26" cy="16" r="2.2" fill="currentColor" />
      <path d="M8 16L23 6" />
    </svg>
  ),
  factory: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 21V9l4 2.5V9l4 2.5V6h8v15z" />
      <path d="M8 21v-3M12 21v-3M16 21v-3" />
    </svg>
  ),
  engine: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.4}
      strokeLinejoin="round"
    >
      <rect x="3" y="9" width="14" height="9" rx="1" />
      <path d="M17 12h3l1 3v3h-4M6 9V6h5v3" />
      <circle cx="7.5" cy="20" r="1.5" />
      <circle cx="15" cy="20" r="1.5" />
    </svg>
  ),
  pylon: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
    >
      <path d="M7 3l-3 18M17 3l3 18M6 8h12M5.5 13h13M12 3v18" />
    </svg>
  ),
  loadDse: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinejoin="round"
    >
      <path d="M4 21V9l5 3V9l5 3V6h6v15z" />
    </svg>
  ),
  sine: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M7 13c1.5-3 3.5-3 5 0s3.5 3 5 0" />
    </svg>
  ),
};

export function ControllerPanel(): JSX.Element {
  const { api, session } = useAuth();
  const { id = '' } = useParams();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [tick, setTick] = useState(false);
  const [ask, setAsk] = useState<{ action: string; label: string } | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [cmd, setCmd] = useState<CmdState>({ kind: 'idle' });
  const timer = useRef<number | null>(null);

  const canControl = !!session && session.role !== 'viewer';

  const load = useCallback(async () => {
    try {
      const p = await api.getControllerPanel(id);
      setPanel(p);
      setErr(null);
      setTick((t) => !t);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not reach the controller.');
    }
  }, [api, id]);

  useEffect(() => {
    void load();
    timer.current = window.setInterval(() => void load(), POLL_MS);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [load]);

  // Issue a command and poll the ack round-trip (never optimistic).
  const runCommand = useCallback(
    async (action: string, label: string, confirm: string) => {
      setCmd({ kind: 'sending', action: label });
      try {
        const { commandId } = await api.issueCommand(id, action, confirm);
        for (let i = 0; i < 15; i++) {
          await sleep(1000);
          const c = await api.getCommand(commandId);
          if (c.status !== 'pending') {
            const ok = c.status === 'confirmed';
            setCmd({
              kind: 'done',
              ok,
              msg: ok ? `${label} confirmed` : (c.error ?? `${label} ${c.status}`),
            });
            void load();
            return;
          }
        }
        setCmd({ kind: 'done', ok: false, msg: `${label}: no confirmation (controller offline?)` });
      } catch (e) {
        setCmd({
          kind: 'done',
          ok: false,
          msg: e instanceof ApiError ? e.message : 'command failed',
        });
      }
    },
    [api, id, load],
  );

  function send() {
    if (!ask) return;
    const { action, label } = ask;
    const confirm = confirmText.trim();
    setAsk(null);
    setConfirmText('');
    void runCommand(action, label, confirm);
  }

  if (err && !panel) return <Banner kind="err">{err}</Banner>;
  if (!panel) return <div className="empty">Connecting to controller…</div>;

  const all = panel.groups.flatMap((g) => g.points);
  const by = (k: string) => all.find((p) => p.key === k);
  const running = (by('engine_speed')?.value ?? 0) > 0;
  const mainsHealthy = (by('mains_l1_n_voltage')?.value ?? 0) > 100;
  const hasAlarm = panel.alarms.length > 0;
  const mode = panel.mode.enumLabel ?? '—';
  const stateLabel = running ? 'ENGINE RUNNING' : mainsHealthy ? 'ON MAINS' : 'STANDBY';
  const site = panel.controller.site_name;
  const norm = (s: string) => s.trim().toLowerCase();
  const confirmOk =
    norm(confirmText) === norm(site) || norm(confirmText) === norm(panel.controller.site_code);

  const pages: { title: string; rows: Row[] }[] = [
    {
      title: 'STATUS',
      rows: [
        { lbl: 'Mode', val: mode },
        { lbl: 'Status', val: stateLabel, big: true },
        { lbl: 'Engine', val: fmt(by('engine_speed')) },
        { lbl: 'Battery', val: fmt(by('engine_battery_voltage')) },
        { lbl: 'Fuel', val: fmt(by('fuel_level')) },
      ],
    },
    ...panel.groups.map((g) => ({
      title: g.title.toUpperCase(),
      rows: g.points.map((p) => ({ lbl: p.label, val: fmt(p) })),
    })),
    {
      title: 'ALARMS',
      rows: hasAlarm
        ? panel.alarms.map((a) => ({ lbl: a.severity.toUpperCase(), val: a.label }))
        : [{ lbl: '', val: 'NO ACTIVE ALARMS' }],
    },
  ];
  const idx = ((page % pages.length) + pages.length) % pages.length;
  const cur = pages[idx]!;
  const flip = (d: number) => setPage((p) => p + d);
  const busy = cmd.kind === 'sending';

  // Which operating mode is active (for the mode-key LEDs), the facia skin, and the shared screen rows.
  const modeStop = mode === 'Stop' || mode === 'Off';
  const modeManual = mode === 'Manual';
  const modeAuto = mode.startsWith('Auto');
  const modeTest = mode.includes('Test');
  const isAts = panel.controller.device_class === 'ats';
  const isSmartGen = (panel.controller.profile_id ?? '').toLowerCase().startsWith('hgm');
  // A source is "live" when it is actually presenting voltage, not when a register merely exists.
  // Null stays null: an unknown source is drawn grey, never as dead, because "we cannot see it" and
  // "it has failed" are the two things an operator must never have to guess between.
  const live = (v: number | null | undefined): boolean | null =>
    v === null || v === undefined ? null : v > 50;
  const s1Live = live(by('source1_l1_n_voltage')?.value);
  const s2Live = live(by('source2_l1_n_voltage')?.value);
  const positionLabel = by('switch_position')?.enumLabel ?? null;
  // Which breaker the switch is on, read from the label the module itself uses. Deliberately string
  // matching rather than assuming a numeric code: the encoding is per-profile and not yet mapped.
  const onS1 = positionLabel ? /s1|source\s*1|normal|mains/i.test(positionLabel) : null;
  const onS2 = positionLabel ? /s2|source\s*2|emergency|gen/i.test(positionLabel) : null;
  const dseModel = panel.controller.profile_name.replace(/^deep sea( electronics)?\s*/i, '');
  const rows = cur.rows.map((r, i) => (
    <div className={`scr-row ${r.big ? 'big' : ''}`} key={`${r.lbl}-${i}`}>
      <span className="lbl">{r.lbl}</span>
      <span className="val">{r.val}</span>
    </div>
  ));
  // Some modules have no alarm-reset key and are reset by selecting Stop/Reset — which stops the
  // engine and takes the set out of Auto. That cannot be the same one-click button as a reset that
  // touches nothing but the alarm list, so it goes through the typed confirmation instead.
  const resetIsStopSelect = panel.control?.resetViaStopSelect ?? false;
  const resetBlocked = panel.control?.blocked.includes('alarm_reset') ?? false;
  const resetHint = resetIsStopSelect
    ? 'Reset alarms. This controller has no alarm-reset key, so it selects Stop/Reset and then returns the mode — refused while the engine is running.'
    : 'Reset alarms';
  // One handler for every way of asking, so the safeguard cannot depend on which button was pressed.
  const resetAlarms = () =>
    resetIsStopSelect
      ? openAsk('alarm_reset', 'Reset alarms')
      : quick('alarm_reset', 'Reset alarms');
  const note = canControl ? (
    <>
      Every control action requires an operator role, a workspace-level enable, and the site name{' '}
      typed to confirm.{' '}
      {hasAlarm && resetBlocked && (
        <span className="muted">This controller cannot clear alarms remotely.</span>
      )}
    </>
  ) : (
    'Read-only (viewer). Operators can control this genset.'
  );

  const openAsk = (action: string, label: string) => {
    setCmd({ kind: 'idle' });
    setConfirmText('');
    setAsk({ action, label });
  };
  // Instant fire for the low-risk alarm keys — server skips the typed confirm; send the code to satisfy
  // the non-empty field.
  const quick = (action: string, label: string) =>
    void runCommand(action, label, panel.controller.site_code);

  return (
    <>
      <div className="crumbs">
        <Link to="/sites">Sites</Link> <span className="faint">/</span> controller
      </div>
      <div className="page-head">
        <div>
          <h1>{panel.controller.display_name ?? 'Controller'}</h1>
          <div className="lead">
            {site} <span className="faint">·</span> {panel.controller.site_code}
            {panel.position && (
              <>
                {' '}
                <span className="faint">·</span>{' '}
                <a
                  className="coords"
                  href={`https://www.openstreetmap.org/?mlat=${panel.position.lat}&mlon=${panel.position.lon}#map=17/${panel.position.lat}/${panel.position.lon}`}
                  target="_blank"
                  rel="noreferrer"
                  title={
                    panel.position.fix_at
                      ? `Router GPS fix at ${new Date(panel.position.fix_at).toLocaleString()}`
                      : 'Reported by the site router'
                  }
                >
                  {panel.position.lat.toFixed(5)}, {panel.position.lon.toFixed(5)}
                </a>
              </>
            )}
          </div>
          {panel.position?.address && <div className="site-address">{panel.position.address}</div>}
        </div>
        <div className="row-right">
          {canControl && panel.siteActions?.includes('controller_power_cycle') && (
            <button
              className="btn btn-ghost btn-sm"
              disabled={busy}
              title="Break and restore the controller's supply through the site router's relay. Refused while the engine is running."
              onClick={() => openAsk('controller_power_cycle', 'Power-cycle controller')}
            >
              Power-cycle
            </button>
          )}
          {(session?.role === 'engineer' || session?.role === 'tenant_admin') && (
            <Link className="btn btn-ghost btn-sm" to={`/controllers/${id}/engineer`}>
              Engineer mode
            </Link>
          )}
          <span className={`pill ${panel.online ? 'good' : 'idle'}`}>
            <span className="dot" />
            {panel.online ? 'Online' : 'Offline'}
          </span>
          <span className="pill live">
            <span className={`dot ${tick ? 'on' : ''}`} />
            live
          </span>
        </div>
      </div>

      {cmd.kind === 'sending' && (
        <Banner kind="ok">Sending {cmd.action}… awaiting controller confirmation</Banner>
      )}
      {cmd.kind === 'done' && <Banner kind={cmd.ok ? 'ok' : 'err'}>{cmd.msg}</Banner>}

      <ConnectivityWidget controllerId={id} />

      {isAts ? (
        /* ===== ComAp InteliATS2 facia =====
           Laid out as the physical unit is: the single-line runs vertically with source 1 at the
           top, the load in the middle and source 2 at the bottom, a breaker either side of the
           load, and the mode strip on the screen. */
        <div className="fascia inteliats">
          <div className="ats-brand">
            <span className="ats-model">{panel.controller.profile_name}</span>
            <span className="ats-maker">ComAp</span>
          </div>

          <div className="ats-body">
            <div className="ats-mimic">
              <div className={`ats-src ${s1Live === null ? 'unknown' : s1Live ? 'live' : 'dead'}`}>
                <span className="ats-sym">∿</span>
                <span className="ats-src-label">S1</span>
                <span className="ats-src-v">{fmt(by('source1_l1_n_voltage'))}</span>
              </div>
              <div className={`ats-brk ${onS1 === null ? 'unknown' : onS1 ? 'closed' : 'open'}`}>
                <span className="ats-brk-dot" />
              </div>
              <div className="ats-load">
                <span className="ats-load-kw">{fmt(by('load_kw'))}</span>
              </div>
              <div className={`ats-brk ${onS2 === null ? 'unknown' : onS2 ? 'closed' : 'open'}`}>
                <span className="ats-brk-dot" />
              </div>
              <div className={`ats-src ${s2Live === null ? 'unknown' : s2Live ? 'live' : 'dead'}`}>
                <span className="ats-sym">∿</span>
                <span className="ats-src-label">S2</span>
                <span className="ats-src-v">{fmt(by('source2_l1_n_voltage'))}</span>
              </div>
            </div>

            <div className="ats-right">
              <div className="ats-modes">
                {['Off', 'Man', 'Auto'].map((m) => (
                  <span
                    key={m}
                    className={`ats-mode ${mode.toLowerCase() === m.toLowerCase() ? 'on' : ''}`}
                  >
                    {m.toUpperCase()}
                  </span>
                ))}
              </div>
              <div className="ats-screen">
                <div className="dse-scr-head">
                  <span>{cur.title}</span>
                  <span>
                    {idx + 1}/{pages.length}
                  </span>
                </div>
                {rows}
              </div>
              <div className="ats-nav">
                <button onClick={() => flip(-1)} aria-label="previous page">
                  ◄
                </button>
                <button onClick={() => setPage(0)} aria-label="home">
                  ✓
                </button>
                <button onClick={() => flip(1)} aria-label="next page">
                  ►
                </button>
              </div>
            </div>
          </div>

          <div className="ats-position">
            <span className="lbl">Switch position</span>
            <strong>{positionLabel ?? '—'}</strong>
            <span className="lbl">Timer</span>
            <strong>{fmt(by('transfer_timer_remaining'))}</strong>
          </div>

          <div className="facia-note">
            Monitoring only. Transfer is not a command this platform issues — moving load between
            sources is done at the switch.
          </div>
        </div>
      ) : isSmartGen ? (
        /* ===== SmartGen HGM61xx facia ===== */
        <div className="fascia smartgen">
          <div className="sg-badge">SmartGen</div>
          <div className="sg-genset">Genset Controller</div>
          <div className="sg-top">
            <div className="sg-annun">
              <div className={`sg-an alarm ${hasAlarm ? 'on' : ''}`}>
                {ICON.alarmTri}
                <span className="cap">Alarm</span>
              </div>
              <div className={`sg-an status ${running ? 'on' : ''}`}>
                {ICON.status}
                <span className="cap">Status</span>
              </div>
            </div>
            <div className="sg-glass">
              <div className="sg-scr-head">
                <span>{cur.title}</span>
                <span>
                  {idx + 1}/{pages.length}
                </span>
              </div>
              {rows}
            </div>
            <div className="sg-nav">
              <button onClick={() => flip(-1)} aria-label="up">
                {ICON.up}
              </button>
              <div className="row">
                <button onClick={() => setPage(0)} aria-label="home">
                  {ICON.home}
                </button>
                <button disabled aria-label="menu">
                  {ICON.gear}
                </button>
              </div>
              <button onClick={() => flip(1)} aria-label="down">
                {ICON.down}
              </button>
            </div>
          </div>
          <div className="sg-bottom">
            <div className="sg-breaker">
              <div className="sg-bkcol">
                <button
                  className="sg-bk close"
                  disabled
                  title="Breaker close — manual, not remotely wired"
                >
                  {ICON.bkClose}
                </button>
                <span className="cap">Close</span>
              </div>
              <div className="sg-bkcol">
                <button
                  className="sg-bk open"
                  disabled
                  title="Breaker open — manual, not remotely wired"
                >
                  {ICON.bkOpen}
                </button>
                <span className="cap">Open</span>
              </div>
              <div className="sg-mimic">
                {ICON.factory}
                <span className="dash" />
                {ICON.engine}
              </div>
            </div>
            <div className="sg-modekeys">
              <div className={`sg-mk ${modeAuto ? 'active' : ''}`}>
                <span className="led" />
                <div className="keywrap">
                  <button
                    disabled={!canControl || busy}
                    onClick={() => openAsk('mode_auto', 'Auto')}
                  >
                    {ICON.spiral}
                  </button>
                  <span className="cap">Auto</span>
                </div>
              </div>
              <div className={`sg-mk ${modeManual ? 'active' : ''}`}>
                <span className="led" />
                <div className="keywrap">
                  <button
                    disabled={!canControl || busy}
                    onClick={() => openAsk('mode_manual', 'Manual')}
                  >
                    {ICON.hand}
                  </button>
                  <span className="cap">Manual</span>
                </div>
              </div>
              <div className="sg-mk">
                <span className="led" />
                <div className="keywrap">
                  <button
                    className="start"
                    disabled={!canControl || busy}
                    onClick={() => openAsk('engine_start', 'Start')}
                  >
                    {ICON.start}
                  </button>
                  <span className="cap">Start</span>
                </div>
              </div>
              {!resetBlocked && (
                <div className={`sg-mk ${hasAlarm ? 'active' : ''}`}>
                  <span className="led" />
                  <div className="keywrap">
                    <button
                      disabled={!canControl || busy}
                      title={resetHint}
                      onClick={() => resetAlarms()}
                    >
                      {ICON.reset}
                    </button>
                    <span className="cap">Reset</span>
                  </div>
                </div>
              )}
              <div className={`sg-mk ${modeStop ? 'active' : ''}`}>
                <span className="led" />
                <div className="keywrap">
                  <button
                    className="stop"
                    disabled={!canControl || busy}
                    onClick={() => openAsk('mode_stop', 'Stop')}
                  >
                    {ICON.stop}
                  </button>
                  <span className="cap">Stop</span>
                </div>
              </div>
            </div>
          </div>
          <div className="facia-note">{note}</div>
        </div>
      ) : (
        /* ===== DSE 7420 MKII facia ===== */
        <div className="fascia dse7420">
          <div className="dse-top">
            <div className="dse-nav">
              <button onClick={() => flip(-1)} aria-label="up">
                ▲
              </button>
              <div className="row">
                <button onClick={() => flip(-1)} aria-label="left">
                  ◄
                </button>
                <button onClick={() => setPage(0)} aria-label="home">
                  ✓
                </button>
                <button onClick={() => flip(1)} aria-label="right">
                  ►
                </button>
              </div>
              <button onClick={() => flip(1)} aria-label="down">
                ▼
              </button>
            </div>
            <div className="dse-display">
              <div className="dse-titlebar">
                <span className="dse-badge">DSE</span> DEEP SEA ELECTRONICS
                <span className="model">{dseModel}</span>
              </div>
              <div className="dse-glass">
                <div className="dse-screen">
                  <div className="dse-scr-head">
                    <span>{cur.title}</span>
                    <span>
                      {idx + 1}/{pages.length}
                    </span>
                  </div>
                  {rows}
                </div>
                <div className="dse-softkeys">
                  <div className={`dse-sk ${mainsHealthy ? 'on' : ''}`}>
                    <span className="dot" />
                    Mains
                  </div>
                  <div className={`dse-sk ${running ? 'on' : ''}`}>
                    <span className="dot" />
                    Gen
                  </div>
                  {/* Order matches the physical DSE7420 MKII facia: Alarm is the 3rd indicator down. */}
                  <div className={`dse-sk alarm ${hasAlarm ? 'on' : ''}`}>
                    <span className="dot" />
                    Alarm
                  </div>
                  <div className={`dse-sk ${running ? 'on' : ''}`}>
                    <span className="dot" />
                    Load
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="dse-mimic">
            {ICON.pylon}
            <div className="wire">
              <i className={`a ${mainsHealthy ? 'lit' : ''}`} />
              <i className="b" />
            </div>
            <span className="load">{ICON.loadDse}</span>
            <div className="wire">
              <i className="a" />
              <i className={`b ${running ? 'lit' : ''}`} />
            </div>
            {ICON.sine}
          </div>
          <div className="dse-modekeys">
            <div className={`dse-mk ${modeStop ? 'active' : ''}`}>
              <span className="led" />
              <button
                className="stop"
                disabled={!canControl || busy}
                onClick={() => openAsk('mode_stop', 'Stop')}
              >
                {ICON.stop}
              </button>
              <span className="cap">
                Stop
                <br />
                Reset
              </span>
            </div>
            <div className={`dse-mk ${modeManual ? 'active' : ''}`}>
              <span className="led" />
              <button
                disabled={!canControl || busy}
                onClick={() => openAsk('mode_manual', 'Manual')}
              >
                {ICON.hand}
              </button>
              <span className="cap">Manual</span>
            </div>
            <div className={`dse-mk ${modeTest ? 'active' : ''}`}>
              <span className="led" />
              <button
                disabled={!canControl || busy}
                onClick={() => openAsk('mode_test_on_load', 'Test')}
              >
                {ICON.test}
              </button>
              <span className="cap">Test</span>
            </div>
            <div className={`dse-mk ${modeAuto ? 'active' : ''}`}>
              <span className="led" />
              <button disabled={!canControl || busy} onClick={() => openAsk('mode_auto', 'Auto')}>
                {ICON.autoBox}
              </button>
              <span className="cap">Auto</span>
            </div>
            <div className="dse-mk">
              <span className="led" />
              <button disabled={!canControl || busy} onClick={() => quick('alarm_mute', 'Mute')}>
                {ICON.mute}
              </button>
              <span className="cap">Mute</span>
            </div>
            {!resetBlocked && (
              <div className={`dse-mk ${hasAlarm ? 'active' : ''}`}>
                <span className="led" />
                <button
                  disabled={!canControl || busy}
                  title={resetHint}
                  onClick={() => resetAlarms()}
                >
                  {ICON.reset}
                </button>
                <span className="cap">Reset</span>
              </div>
            )}
            <div className="dse-mk">
              <span className="led" />
              <button
                className="start"
                disabled={!canControl || busy}
                onClick={() => openAsk('engine_start', 'Start')}
              >
                {ICON.start}
              </button>
              <span className="cap">Start</span>
            </div>
          </div>
          <div className="facia-note">{note}</div>
        </div>
      )}

      {ask && (
        <div className="modal-overlay" onClick={() => setAsk(null)}>
          <div className="panel modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="card-accent" />
            <div className="panel-pad">
              <div className="section-label" style={{ color: 'var(--amber)' }}>
                Confirm control action
              </div>
              <p style={{ marginTop: 0 }}>
                Send <b>{ask.label}</b> to <b>{panel.controller.display_name}</b>. This operates the
                genset. Type the site name <b>{site}</b> to confirm (not case-sensitive).
              </p>
              {ask.action === 'controller_power_cycle' && (
                <p style={{ marginTop: 0, color: 'var(--amber)' }}>
                  This breaks the controller's supply at the site router for about 15 seconds and
                  then restores it. The controller reboots and the panel goes quiet meanwhile. It is
                  refused while the engine is running.
                </p>
              )}
              {ask.action === 'alarm_reset' && resetIsStopSelect && (
                <p style={{ marginTop: 0, color: 'var(--amber)' }}>
                  This controller has no alarm-reset key, so alarms are cleared by selecting{' '}
                  <b>Stop/Reset</b> — the same thing the front-panel button does. The mode is put
                  back afterwards and checked. It is refused while the engine is running.
                </p>
              )}
              <input
                className="input mono"
                placeholder={site}
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && confirmOk) send();
                }}
                autoFocus
              />
              <div className="form-actions">
                <button className="btn btn-ghost" onClick={() => setAsk(null)}>
                  Cancel
                </button>
                <Button onClick={() => void send()} disabled={!confirmOk}>
                  Send {ask.label}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
